import { PIN_CAP } from '@tunasong/models'
import type { Entity, Favorite, Persisted } from '@tunasong/schemas'

/** The one cap for personal and band pins (GH #2822: one rule to learn), defined beside the band pin logic */
export { PIN_CAP }

export interface PinTarget {
  id: string
  name?: string
  type: Persisted<Entity>['type']
  /** Path under which the entry is stored in user-config favorites. */
  path: string
}

/**
 * Pinned subset of a favorites array, sorted by `pinOrder` asc. Missing `pinOrder`
 * is treated as 0 to keep legacy/unset entries first-stable.
 */
export const getPinnedFavorites = (favorites: Favorite[]): Favorite[] =>
  favorites.filter(f => f.pinned === true).sort((a, b) => (a.pinOrder ?? 0) - (b.pinOrder ?? 0))

export const isPinnedFavorite = (favorites: Favorite[], entityId: string): boolean =>
  favorites.some(f => f.entityId === entityId && f.pinned === true)

/**
 * Add or promote an entry to pinned. No-op if the cap is reached or the entity is
 * already pinned. Returns the same reference on no-op so callers can short-circuit
 * the write to user-config.
 */
export const applyPin = (favorites: Favorite[], target: PinTarget): Favorite[] => {
  if (isPinnedFavorite(favorites, target.id)) {
    return favorites
  }
  const pinnedCount = getPinnedFavorites(favorites).length
  if (pinnedCount >= PIN_CAP) {
    return favorites
  }
  const nextOrder = pinnedCount
  const existingIdx = favorites.findIndex(f => f.path === target.path)
  if (existingIdx >= 0) {
    const next = [...favorites]
    next[existingIdx] = {
      ...next[existingIdx],
      entityId: target.id,
      pinned: true,
      pinOrder: nextOrder,
    }
    return next
  }
  return [
    ...favorites,
    {
      path: target.path,
      name: target.name ?? target.id,
      entityType: target.type,
      entityId: target.id,
      pinned: true,
      pinOrder: nextOrder,
    },
  ]
}

/**
 * Clear pinned + pinOrder for an entry. If the entry was pinned-only (never explicitly
 * favorited), remove it entirely so we don't accumulate orphan favorites from short
 * pin sessions. Returns the same reference if the entityId is not found.
 */
export const applyUnpin = (favorites: Favorite[], entityId: string): Favorite[] => {
  const idx = favorites.findIndex(f => f.entityId === entityId && f.pinned === true)
  if (idx < 0) {
    return favorites
  }
  const entry = favorites[idx]
  // If `favorite` is explicitly true we keep the entry; if it's undefined or false we
  // treat the entry as pin-only and drop it.
  if (entry.favorite === true) {
    const next = [...favorites]
    next[idx] = { ...entry, pinned: false, pinOrder: undefined }
    return next
  }
  return favorites.filter((_, i) => i !== idx)
}

/**
 * Rewrite `pinOrder` on the pinned entries to match the order of the given id list.
 * Ids that are not currently pinned are ignored; non-pinned favorites are passed through.
 */
export const applyReorderPins = (favorites: Favorite[], orderedIds: string[]): Favorite[] => {
  const pinned = getPinnedFavorites(favorites)
  const knownIds = new Set(pinned.map(f => f.entityId).filter((id): id is string => typeof id === 'string'))
  const sequence = orderedIds.filter(id => knownIds.has(id))
  return favorites.map(f => {
    if (f.pinned !== true || typeof f.entityId !== 'string') {
      return f
    }
    const newOrder = sequence.indexOf(f.entityId)
    if (newOrder < 0) {
      return f
    }
    return { ...f, pinOrder: newOrder }
  })
}
